import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import axios from 'axios';
import qs from 'qs';
import { Status } from '../../models/common/Status';
import { RootState } from '../store';
import { apiClientWithApi } from '../../utils/http';
import { IncomingStatisticsParameters } from '../../hooks/components/useIncomingStatistics/constants';
import { buildTaskTypeOptions } from '../../models/Task/scenarioMapping';
import { loadInboundTaskTypeDictionary } from '../../services/taskTypeDictionary';
import type { TaskCampaignFilters } from '../../models/Task/campaignFilters';

export interface InboundTaskTypeOption {
  value: string;
  label: string;
}

export interface InboundTaskTypesPayload {
  options: InboundTaskTypeOption[];
}

export interface IncomingStatisticsState {
  confirmedTasksCount: number;
  cancelledTasksCount: number;
  waitingListTasksCount: number;
  cancelledByClinicTasksCount: number;
  rescheduledTasksCount: number;
  rescheduledByClinicTasksCount: number;
  operatorHandedTasksCount: number;
  notIdentifiedTasksCount: number;
  notReceivedTasksCount: number;
  blockedTasksCount: number;
  totalCount: number;
  primaryCount: number;
  errorCount: number;
  hangUpCount: number;
  inboundTaskTypeOptions: InboundTaskTypeOption[];
  currentRequestId?: string;
  lastSuccessfulRequest?: IncomingStatisticsRequest;
  status: Status;
}

export interface InboundStatistic {
  confirmedTasksCount: number;
  cancelledTasksCount: number;
  waitingListTasksCount: number;
  cancelledByClinicTasksCount: number;
  rescheduledTasksCount: number;
  rescheduledByClinicTasksCount: number;
  operatorHandedTasksCount: number;
  notIdentifiedTasksCount: number;
  notReceivedTasksCount: number;
  blockedTasksCount: number;
  totalCount: number;
  primaryCount: number;
  errorCount: number;
  hangUpCount: number;
}

export interface IncomingStatisticsRequest extends TaskCampaignFilters {
  From: string;
  To: string;
  ClinicIds?: string[];
  States?: string[];
  AttemptStates?: string[];
  Result?: string[];
  Types?: string[];
  TextSearch?: string;
}

const initialState: IncomingStatisticsState = {
  confirmedTasksCount: 0,
  cancelledTasksCount: 0,
  waitingListTasksCount: 0,
  cancelledByClinicTasksCount: 0,
  rescheduledTasksCount: 0,
  rescheduledByClinicTasksCount: 0,
  operatorHandedTasksCount: 0,
  notIdentifiedTasksCount: 0,
  notReceivedTasksCount: 0,
  blockedTasksCount: 0,
  totalCount: 0,
  primaryCount: 0,
  errorCount: 0,
  hangUpCount: 0,
  inboundTaskTypeOptions: [],
  status: {
    responseLoading: true,
    responseError: null,
    requestSuccess: null,
    requestError: null,
  },
};

const buildIncomingStatisticsParams = (request: IncomingStatisticsRequest) => {
  const params: Record<string, string | string[]> = {
    [IncomingStatisticsParameters.From]: request.From,
    [IncomingStatisticsParameters.To]: request.To,
  };

  if (request.ClinicIds?.length) {
    params[IncomingStatisticsParameters.ClinicIds] = request.ClinicIds;
  }

  if (request.States?.length) {
    params.States = request.States;
  }

  if (request.AttemptStates?.length) {
    params.AttemptStates = request.AttemptStates;
  }

  if (request.Result?.length) {
    params.Result = request.Result;
  }

  if (request.Types?.length) {
    params.Types = request.Types;
  }

  if (request.FormBatchIds?.length) {
    params.FormBatchIds = request.FormBatchIds;
  }

  if (request.DispensarizationBatchIds?.length) {
    params.DispensarizationBatchIds = request.DispensarizationBatchIds;
  }

  if (request.FormTemplateIds?.length) {
    params.FormTemplateIds = request.FormTemplateIds;
  }

  if (request.TextSearch) {
    params.TextSearch = request.TextSearch;
  }

  return params;
};

export const getIncomingStatistics = createAsyncThunk(
  'IncomingStatistics/getIncomingStatistics',
  async (request: IncomingStatisticsRequest, { rejectWithValue, signal }) => {
    try {
      const response = await apiClientWithApi.get<InboundStatistic>(
        'task/statistic/inbound',
        {
          params: buildIncomingStatisticsParams(request),
          paramsSerializer: function (params) {
            return qs.stringify(params, { arrayFormat: 'repeat' });
          },
          signal,
        },
      );

      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(error.message);
      } else return rejectWithValue('Не удалось получить статистику!');
    }
  },
);

export const getInboundTaskTypes = createAsyncThunk<InboundTaskTypesPayload>(
  'IncomingStatistics/getInboundTaskTypes',
  async () => {
    const fallback: InboundTaskTypesPayload = { options: [] };

    try {
      const dictionary = await loadInboundTaskTypeDictionary();
      if (!Array.isArray(dictionary) || dictionary.length === 0) {
        return fallback;
      }

      const options = buildTaskTypeOptions(dictionary);
      return options.length > 0 ? { options } : fallback;
    } catch {
      return fallback;
    }
  },
);

export const incomingStatisticsSlice = createSlice({
  name: 'incomingStatistics',
  initialState,
  reducers: {},
  extraReducers(builder) {
    builder
      .addCase(getIncomingStatistics.pending, (state, action) => {
        state.status.responseLoading = true;
        state.status.responseError = null;
        state.currentRequestId = action.meta.requestId;
        return state;
      })
      .addCase(getIncomingStatistics.fulfilled, (state, action) => {
        if (state.currentRequestId !== action.meta.requestId) {
          return state;
        }

        state.status.responseLoading = false;
        state.status.responseError = null;
        state.currentRequestId = undefined;
        Object.assign(state, action.payload);
        state.lastSuccessfulRequest = action.meta.arg;
        return state;
      })
      .addCase(getIncomingStatistics.rejected, (state, action) => {
        if (state.currentRequestId !== action.meta.requestId) {
          return state;
        }

        state.status.responseLoading = false;
        state.currentRequestId = undefined;
        state.status.responseError = action.meta.aborted
          ? null
          : 'Не удалось загрузить звонки';
        return state;
      })
      .addCase(getInboundTaskTypes.fulfilled, (state, action) => {
        state.inboundTaskTypeOptions = action.payload.options;
        return state;
      });
  },
});

export default incomingStatisticsSlice.reducer;

export const selectInboundTaskTypeOptions = (state: RootState) =>
  state.incomingStatistics.inboundTaskTypeOptions;
